<script setup lang="ts">
import { SentenceGroupType, SentenceType } from 'types/types'
import { computed, onMounted, reactive, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import AnnotatedText from '@/components/AnnotatedText.vue'
import SentenceGroupSelect from '@/components/SentenceGroupSelect.vue'
import { useUtil } from '@/composables/util'
import { ROOT_USERNAME } from '@/constants/appConstants'
import SetResponsivenessOnSentence from '@/mutations/SetResponsivenessOnSentence.graphql'
import store from '@/store'
import { runReportedMutation } from '@/store/mutationHelpers'
import { AnnotatedSentence } from '@/util/AnnotatedSentence'

interface CustomResponsivenessData {
  selectedSentenceGroup: SentenceGroupType | null
  annotatedSentences: Array<AnnotatedSentence> | null
}

const state = reactive<CustomResponsivenessData>({
  annotatedSentences: null,
  selectedSentenceGroup: null,
})

const { isStaff } = useUtil(useLogger())

// Shared (root-owned) texts are staff-only: a change there reaches every teacher's students.
const canEdit = computed(() => isStaff() || state.selectedSentenceGroup?.reading.user.username !== ROOT_USERNAME)

onMounted(() => {
  store.dispatch.user.fetchUser()

  watchEffect(() => {
    if (state.selectedSentenceGroup) {
      store.dispatch.student.fetchSentenceGroupSentences({
        sentenceGroup: state.selectedSentenceGroup,
      })
    }
  })
})

const allSentenceGroupSentences = computed<Readonly<Record<number, Array<SentenceType>>>>(
  (): Readonly<Record<number, Array<SentenceType>>> => {
    return store.getters.student.sentenceGroupSentences
  },
)

const sentenceGroupSentences = computed<Array<SentenceType>>((): Array<SentenceType> => {
  if (!state.selectedSentenceGroup) {
    return []
  }
  return (allSentenceGroupSentences.value[parseInt(state.selectedSentenceGroup.reading.id)] ?? []).toSorted(
    (a: SentenceType, b: SentenceType) => parseInt(a.id, 10) - parseInt(b.id, 10),
  )
})

async function flipResponsiveness(annotatedSentence: AnnotatedSentence, __: number) {
  if (!canEdit.value) {
    return
  }
  annotatedSentence.setIsUpdating(true)
  try {
    await runReportedMutation(
      SetResponsivenessOnSentence,
      {
        responsive: !annotatedSentence.sentence().isResponsive,
        sentence: annotatedSentence.sentence().id,
      },
      'setResponsivenessOnSentence',
      store.dispatch.snackbar.showError,
    )
  } finally {
    await store.dispatch.student.fetchSentenceGroupSentences({
      sentenceGroup: state.selectedSentenceGroup as SentenceGroupType,
    })
    annotatedSentence.setIsUpdating(false)
  }
}
</script>

<template>
  <v-container fluid>
    <v-row>
      <v-col md="4">
        <sentence-group-select v-model:sentence-group="state.selectedSentenceGroup" />
      </v-col>
    </v-row>
    <v-row v-if="state.selectedSentenceGroup && !canEdit">
      <v-col md="8">
        <v-alert type="info" variant="tonal">This is a shared text. Only staff can change its call and response.</v-alert>
      </v-col>
    </v-row>
    <v-row v-if="sentenceGroupSentences && sentenceGroupSentences.length" class="mt-3 pt-3">
      <v-col md="12">
        <v-card>
          <v-card-text>
            <v-row justify="center">
              <v-col class="text-center" md="11">
                <annotated-text
                  v-if="state.selectedSentenceGroup"
                  v-model:annotated-sentences="state.annotatedSentences as Array<AnnotatedSentence>"
                  :break-before-line="() => true"
                  :clickable-line-number="() => canEdit"
                  :clickable-responsive-header="() => canEdit"
                  :clickable-sentence="() => canEdit"
                  name="text"
                  :sentences="sentenceGroupSentences"
                  :show-responsive-headers="() => true"
                  :show-sentence-numbers="true"
                  @click-line-number="flipResponsiveness"
                  @click-responsive-header="flipResponsiveness"
                  @click-sentence="flipResponsiveness"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.annotated-scroller {
  min-height: 200px;
  height: calc(100vh - 450px);
}
</style>
